import React from 'react';
import { ChevronLeft } from 'lucide-react';
import { cn } from '../../../lib/utils';
import ButtonV2, { ButtonV2Props } from '../button/v2';

interface ActionConfig extends ButtonV2Props {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  variant: 'primary' | 'secondary';
}

interface PageNavigationHeaderProps {
  title?: string;
  subtitle?: string;
  breadcrumb?: {
    label: string;
    onClick: () => void;
  };
  actions?: ActionConfig[];
  className?: string;
  version?: "1" | "2";
}

const PageNavigationHeader: React.FC<PageNavigationHeaderProps> = ({
  title,
  subtitle,
  breadcrumb,
  actions = [],
  className,
  version = "1"
}) => {
  const buttonBaseStyles = "inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed";
  
  const buttonVariantStyles = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white border border-blue-600",
    secondary: "bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 hover:border-gray-300"
  };

  return (
    <div className={cn(
      'border border-gray-200 rounded-lg bg-white mb-6',
      className,
      version === "2" && "border-0 bg-transparent shadow-none"
    )}>
      <div className={cn("px-6 py-4", version === "2" ? "p-0" : "")}>
        {/* Single row with breadcrumb and actions */}
        <div className="flex items-center justify-between gap-4">
          {/* Left side - Breadcrumb */}
          <div className="flex items-center">
            {breadcrumb && (
              <button
                onClick={breadcrumb.onClick}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-blue-700 transition-colors duration-200 group"
              >
                <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
                <span className="font-semibold">{breadcrumb.label}</span>
              </button>
            )}
          </div>

          {/* Right side - Actions */}
          {actions.length > 0 && (
            <div className="flex items-center gap-3 flex-shrink-0">
              {actions.map((action, index) => {
                const Icon = action.icon;
                const variant = action.variant || 'secondary';
                
                if (version === '2') {
                  return (
                    <ButtonV2
                      key={index}
                      onClick={action.onClick}
                      className={cn(buttonBaseStyles)}
                      prefix={Icon ? <Icon className="w-4 h-4" /> : undefined}
                      {...action}
                    >
                      {/* {Icon && <Icon className="w-4 h-4" />} */}
                      {action.label}
                    </ButtonV2>
                  );
                }

                return (
                  <button
                    key={index}
                    onClick={action.onClick}
                    className={cn(
                      buttonBaseStyles,
                      buttonVariantStyles[variant]
                    )}
                  >
                    {Icon && <Icon className="w-4 h-4" />}
                    {action.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Optional title section below (if needed) */}
        {title && (
          <div className="mt-4">
            <h1 className="text-2xl font-semibold text-gray-900">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-1 text-sm text-gray-600">
                {subtitle}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageNavigationHeader;